import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  DeliveredCouponStatRow,
  searchDeliveredCouponsForStats,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { DELIVERY_CHANNEL_CONFIG } from '@shared/api/giftCoupon/giftCoupon.interface';

import { CenterLoading, ErrorBox, TD_CLASS, TH_CLASS } from '../../components/DashboardShared';
import { fmt, getDatePreset, getLocalToday, toDateStr } from './analysisUtils';
import {
  STATS_CHANNEL_UNSPECIFIED,
  StatsChannelKey,
  StatsPeriodUnit,
  buildDeliveryStats,
} from './deliveryStatsUtils';

const channelLabel = (ch: StatsChannelKey): string =>
  ch === STATS_CHANNEL_UNSPECIFIED ? '미기재' : DELIVERY_CHANNEL_CONFIG[ch].label;

const channelBadgeClass = (ch: StatsChannelKey): string =>
  ch === STATS_CHANNEL_UNSPECIFIED
    ? 'border-gray-300 bg-gray-50 text-gray-600'
    : DELIVERY_CHANNEL_CONFIG[ch].badgeClass;

interface Props {
  from?: string;
  to?: string;
}

/**
 * 발송 통계 탭 — 기간 내 발송완료(Delivered) 쿠폰을 매체·기간·상품 축으로 집계.
 * 데이터가 무거울 수 있어 이 탭이 열릴 때만 lean 조회한다 (페이지 공용 주문 조회와 별개).
 * 금액은 발송 시 기록된 도매가(gifticon.priceWhole) 기준.
 */
export const DeliveryStatsTab: FC<Props> = function DeliveryStatsTab({ from, to }) {
  // 기간 필터가 비어있으면(직접선택 미적용 등) 최근 3개월
  const fallback = useMemo(() => getDatePreset('3m'), []);
  const effFrom = from ?? fallback.from ?? toDateStr(getLocalToday());
  const effTo = to ?? toDateStr(getLocalToday());

  const [rows, setRows] = useState<DeliveredCouponStatRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unit, setUnit] = useState<StatsPeriodUnit>('week');
  const [productChannel, setProductChannel] = useState<'all' | StatsChannelKey>('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await searchDeliveredCouponsForStats(effFrom, effTo));
    } catch (e) {
      setError(e instanceof Error ? e.message : '발송 내역을 불러올 수 없습니다.');
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, [effFrom, effTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const stats = useMemo(
    () => (rows ? buildDeliveryStats(rows, unit) : null),
    [rows, unit],
  );

  // 기간 변경 등으로 사라진 매체가 필터에 남지 않게 보정
  useEffect(() => {
    if (
      productChannel !== 'all' &&
      stats &&
      !stats.channelsPresent.includes(productChannel)
    ) {
      setProductChannel('all');
    }
  }, [stats, productChannel]);

  const productRows = useMemo(() => {
    if (!stats) return [];
    if (productChannel === 'all') {
      return stats.products.map((p) => ({ key: p.key, name: p.name, ...p.total }));
    }
    return stats.products
      .map((p) => {
        const agg = p.byChannel[productChannel];
        return agg ? { key: p.key, name: p.name, ...agg } : null;
      })
      .filter((p): p is { key: string; name: string; count: number; amount: number } =>
        Boolean(p),
      )
      .sort((a, b) => b.amount - a.amount);
  }, [stats, productChannel]);

  if (loading) return <CenterLoading />;
  if (error) return <ErrorBox message={error} onRetry={fetchData} />;
  if (!stats || stats.totalCount === 0) {
    return (
      <div className="rounded-lg bg-white p-8 text-center shadow">
        <p className="text-gray-500">해당 기간에 발송 내역이 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 컨트롤 + 총계 */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1">
          {(['week', 'month'] as StatsPeriodUnit[]).map((u) => (
            <button
              key={u}
              onClick={() => setUnit(u)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                unit === u
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {u === 'week' ? '주별' : '월별'}
            </button>
          ))}
        </div>
        <p className="text-sm text-gray-600">
          총 <span className="font-bold text-gray-900">{fmt(stats.totalCount)}건</span>{' '}
          · <span className="font-bold text-gray-900">{fmt(stats.totalAmount)}원</span>
          <span className="ml-1 text-xs text-gray-400">(도매가 기준)</span>
        </p>
      </div>

      {/* 매체별 합계 카드 */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {stats.channelsPresent.map((ch) => {
          const agg = stats.channelTotals[ch];
          if (!agg) return null;
          const share =
            stats.totalAmount > 0 ? (agg.amount / stats.totalAmount) * 100 : 0;
          return (
            <div key={ch} className="rounded-lg bg-white p-4 shadow">
              <span
                className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${channelBadgeClass(
                  ch,
                )}`}
              >
                {channelLabel(ch)}
              </span>
              <p className="mt-2 text-xl font-bold text-gray-900">
                {fmt(agg.amount)}원
              </p>
              <p className="mt-0.5 text-xs text-gray-400">
                {fmt(agg.count)}건 · {share.toFixed(1)}%
              </p>
            </div>
          );
        })}
      </div>

      {/* 기간별 추이 */}
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">
            {unit === 'week' ? '주별' : '월별'} 매체 발송 추이
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={TH_CLASS}>기간</th>
                {stats.channelsPresent.map((ch) => (
                  <th key={ch} className={`${TH_CLASS} text-right`}>
                    {channelLabel(ch)}
                  </th>
                ))}
                <th className={`${TH_CLASS} text-right`}>합계</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stats.periods.map((period) => (
                <tr key={period.key} className="hover:bg-gray-50">
                  <td className={`${TD_CLASS} font-medium`}>{period.label}</td>
                  {stats.channelsPresent.map((ch) => {
                    const agg = period.byChannel[ch];
                    return (
                      <td key={ch} className={`${TD_CLASS} text-right`}>
                        {agg ? (
                          <>
                            {fmt(agg.amount)}원{' '}
                            <span className="text-xs text-gray-400">
                              ({fmt(agg.count)})
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                    );
                  })}
                  <td className={`${TD_CLASS} text-right font-bold`}>
                    {fmt(period.total.amount)}원{' '}
                    <span className="text-xs font-normal text-gray-400">
                      ({fmt(period.total.count)})
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 상품별 발송 */}
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">
            상품별 발송 ({fmt(productRows.length)}종)
          </h3>
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => setProductChannel('all')}
              className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                productChannel === 'all'
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              전체
            </button>
            {stats.channelsPresent.map((ch) => (
              <button
                key={ch}
                onClick={() => setProductChannel(ch)}
                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                  productChannel === ch
                    ? 'ring-2 ring-indigo-300 ' + channelBadgeClass(ch)
                    : channelBadgeClass(ch)
                }`}
              >
                {channelLabel(ch)}
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-[420px] overflow-x-auto overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className={TH_CLASS}>상품명</th>
                <th className={`${TH_CLASS} text-right`}>건수</th>
                <th className={`${TH_CLASS} text-right`}>금액</th>
                <th className={`${TH_CLASS} text-right`}>평균단가</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {productRows.map((p) => (
                <tr key={p.key} className="hover:bg-gray-50">
                  <td className={`${TD_CLASS} max-w-[360px]`}>
                    <span className="block truncate" title={p.name}>
                      {p.name}
                    </span>
                  </td>
                  <td className={`${TD_CLASS} text-right`}>{fmt(p.count)}건</td>
                  <td className={`${TD_CLASS} text-right font-medium`}>
                    {fmt(p.amount)}원
                  </td>
                  <td className={`${TD_CLASS} text-right text-gray-500`}>
                    {p.count > 0 ? fmt(Math.round(p.amount / p.count)) : 0}원
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
